import React, { Fragment, useCallback } from 'react';

import AnimateHeight from 'react-animate-height';
import { useDispatch, useSelector } from 'react-redux';

import { colors } from '@atlaskit/theme';
import * as styles from '@atlassian/bitkit-diff/exports/styles';

import {
  apiCommentId,
  toConversationId,
} from 'src/components/conversation-provider/utils/to-conversation';
import { CommentThread } from 'src/components/conversation/src/components/comment-tree';
import { useConversationsContext } from 'src/contexts/conversations-context';
import { CLOSE_FILE_COMMENT } from 'src/redux/pull-request/actions';
import {
  getHighlightCommentThread,
  getHighlighted,
  isFileCommentOpen,
} from 'src/redux/pull-request/selectors';
import { CommentThreadWrapper } from 'src/sections/repository/styles/page';
import { getFileLevelThreadsForFile } from 'src/selectors/conversation-selectors';
import { Diff } from 'src/types/pull-request';
import { BucketState } from 'src/types/state';
import { extractFilepath } from 'src/utils/extract-file-path';

import { useHiddenComments } from '../../hooks/diffs';

import {
  NewPullRequestConversation,
  PullRequestConversation,
} from './pull-request-conversation';

type Props = {
  editorPopupsMountPoint?: HTMLElement | null;
  file: Diff;
};

export const BasePullRequestFileConversation = ({
  editorPopupsMountPoint,
  file,
}: Props): JSX.Element | null => {
  const path = extractFilepath(file);
  const conversationsContext = useConversationsContext();
  const dispatch = useDispatch();

  const handleCancel = useCallback(() => {
    dispatch({ type: CLOSE_FILE_COMMENT, payload: path });
  }, [dispatch, path]);

  const fileThreads = useSelector<BucketState, CommentThread[]>(state =>
    getFileLevelThreadsForFile(state, file)
  );

  const isCommentThreadHighlighted = useSelector(getHighlightCommentThread);
  const highlightedCommentId = useSelector(getHighlighted);

  const areCommentsHidden = useHiddenComments(file);

  const isCommenting = useSelector<BucketState, boolean>(state =>
    isFileCommentOpen(state, path)
  );

  const onEditorOpen = useCallback(() => {
    if (
      typeof conversationsContext?.eventHandlers?.onNewConversationClick ===
      'function'
    ) {
      conversationsContext?.eventHandlers?.onNewConversationClick({
        file: path,
      });
    }
  }, [conversationsContext?.eventHandlers, path]);

  const shouldHighlightCurrentConversation = () => {
    const conversationIds = fileThreads.map(thread =>
      apiCommentId(thread.topComment)
    );

    if (!(isCommentThreadHighlighted && highlightedCommentId)) {
      return false;
    }

    return conversationIds.includes(String(highlightedCommentId));
  };

  return fileThreads.length === 0 && !isCommenting ? null : (
    <AnimateHeight
      duration={250}
      animateOpacity
      height={areCommentsHidden ? 0 : 'auto'}
    >
      <styles.TopLevelInlineContent>
        <styles.InlineContentContainer>
          <CommentThreadWrapper
            style={
              shouldHighlightCurrentConversation()
                ? { borderColor: `${colors.B100}` }
                : {}
            }
          >
            <React.Fragment>
              {fileThreads.map(thread => {
                const conversationId = toConversationId(thread.topComment);
                return (
                  <Fragment
                    key={`${conversationId}-${thread.topComment.created_on}`}
                  >
                    <PullRequestConversation
                      editorPopupsMountPoint={editorPopupsMountPoint}
                      canCollapseThread
                      canResolveThread={
                        conversationsContext.supportsCommentResolution
                      }
                      meta={{
                        path,
                      }}
                      portalParentId={`portal-parent-pr-file-conversation-${conversationId}`}
                      commentThreads={[thread]}
                    />
                  </Fragment>
                );
              })}
              {isCommenting && (
                <NewPullRequestConversation
                  editorPopupsMountPoint={editorPopupsMountPoint}
                  onCancel={handleCancel}
                  onEditorOpen={onEditorOpen}
                  meta={{
                    path: extractFilepath(file),
                  }}
                  portalParentId="portal-parent-pr-file-conversation-new"
                  commentThreads={[]}
                />
              )}
            </React.Fragment>
          </CommentThreadWrapper>
        </styles.InlineContentContainer>
      </styles.TopLevelInlineContent>
    </AnimateHeight>
  );
};

export const PullRequestFileConversation = React.memo(
  BasePullRequestFileConversation
);
